import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

const NOTES_PER_PAGE = 5

const NoteHistory = ({ noteHistory, returnHandler = () => null }) => {
  const [currentPage, setCurrentPage] = useState(1)

  if (!noteHistory || noteHistory.length === 0) {
    return null
  }

  // Note: We already keep a list of users in redux which includes deleted users,
  // so we can use that to translate user IDs to display names without needing to make additional API calls.
  const users = useSelector(state => state.appConfig?.users) || []
  const getUserById = (id) => {
    if (!id) {
      return undefined
    }
    return users.find(user => user?.user_id === id)
  }
  const userMap = useMemo(() => {
    const ids = Array.from(
      new Set(
        noteHistory
          .map(note => note.created_by)
          .filter(Boolean)
      )
    )
    return ids.reduce((map, id) => {
      map[id] = getUserById(id)
      return map
    }, {})
  }, [noteHistory, users])

  const enhancedNoteHistory = useMemo(() => {
    return noteHistory.map(note => {
      const createdById = typeof note.created_by === 'object'
        ? note.created_by?.user_id
        : note.created_by
      return {
        ...note,
        created_by: userMap[createdById] || note.created_by
      }
    })
  }, [noteHistory, userMap])

  const totalPages = Math.ceil(enhancedNoteHistory.length / NOTES_PER_PAGE)
  const startIndex = (currentPage - 1) * NOTES_PER_PAGE
  const endIndex = startIndex + NOTES_PER_PAGE
  const currentNotes = enhancedNoteHistory.slice(startIndex, endIndex)

  const goToFirstPage = () => setCurrentPage(1)
  const goToLastPage = () => setCurrentPage(totalPages)
  const goToPrevPage = () => setCurrentPage(Math.max(1, currentPage - 1))
  const goToNextPage = () => setCurrentPage(Math.min(totalPages, currentPage + 1))
  const goToPage = (page) => setCurrentPage(page)

  const renderPageNumbers = () => {
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`note-history-page-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => goToPage(i)}
        >
          {i}
        </button>
      )
    }
    return pages
  }

  return (
    <div className="note-history">
      <h3>Note History</h3>
      <div className="note-history-items">
        {currentNotes.map((note, index) => {
          const actualIndex = startIndex + index
          return (
            <div
              key={note.note_id}
              className={
                actualIndex === 0
                  ? 'note-history-item note-history-item-current'
                  : 'note-history-item'
              }
            >
              <div className="note-history-content">{note.content}</div>
              <div className="note-history-meta">
                <span className="note-history-author">
                  {note?.created_by?.displayname || 'unknown'}
                </span>
                <span className="note-history-timestamp">
                  {new Date(note.created_at).toLocaleString()}
                </span>
                {actualIndex === 0 ? (
                  <span className="note-history-current">Current</span>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
      <div className="note-history-footer">
        <div></div>
        {totalPages > 1 && (
          <div className="note-history-pagination">
            <button
              className="note-history-page-nav"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
            >
              &laquo;
            </button>
            <button
              className="note-history-page-nav"
              onClick={goToPrevPage}
              disabled={currentPage === 1}
            >
              &lsaquo;
            </button>
            {renderPageNumbers()}
            <button
              className="note-history-page-nav"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              &rsaquo;
            </button>
            <button
              className="note-history-page-nav"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
            >
              &raquo;
            </button>
          </div>
        )}
        <button className="note-history-back-button" onClick={returnHandler}>
          Back
        </button>
      </div>
    </div>
  )
}

export default NoteHistory